import React, { useState, memo } from "react";

import { Dialog, Grid, Table, TableBody, TableHead, TableRow, Typography } from "@mui/material";

import { Props } from "./interfaces";
import { Container, DialogContent, StyledTableCell, TableContainer, TextMessage } from "./styles";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import { SKILLS_CATEGORIES } from "../../../../utils/constants/constants";
import { useTranslation } from "react-i18next";
import DataActions from "../../Candidates/SkillsDetailsModal/DataActions/DataActions";
import { AddEditRequiredPositionSkills } from "../AddEditRequiredPositionSkills/AddEditRequiredPositionSkills";

const PositionSkillsDetailsModal = ({ children, notifyParent, position, skillCategory }: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.system_settings.tables.candidate_skills";

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const categoryName = skillCategory.name;

  const currSkills =
    position.requiredSkills && position.requiredSkills.length !== 0
      ? position.requiredSkills.filter((currSkill) => {
          if (currSkill.skill && currSkill.skill.category) return currSkill.skill.category.id === skillCategory.id;
        })
      : [];

  const getWidth = (fieldTitle: string): number => {
    const field = columns.find((obj) => obj.field === fieldTitle);

    if (field) return field.width;

    return 0;
  };

  const columns = [
    {
      title: t(`${tRP}.fields.skills`),
      field: "skill",
      width: 40,
    },
    {
      title: t(`${tRP}.fields.skill_level`),
      field: "skillLevel",
      width: 40,
    },
    {
      title: t(`${tRP}.fields.years_of_experience`),
      field: "yearsOfExperience",
      width: 20,
    },
    {
      title: "",
      field: "actions",
      width: 0,
    },
  ];

  return (
    <>
      <Grid
        style={{ display: "flex", alignItems: "center" }}
        onClick={() => setOpen(true)}
      >
        {children}
      </Grid>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          title={`${categoryName} skills`}
          handleClose={() => setOpen(false)}
        />
        <LoadingOverlay loading={loading}>
          <DialogContent>
            {currSkills?.length === 0 ? (
              <TextMessage>There are no skills</TextMessage>
            ) : (
              <>
                <Container
                  style={{
                    width: "100%",
                    minHeight: "200px",
                    maxHeight: "600px",
                    marginBottom: "20px",
                  }}
                >
                  <TableContainer>
                    {
                      <Table
                        size={"small"}
                        stickyHeader={true}
                      >
                        <TableHead>
                          <TableRow>
                            {columns.map((column, idx) => (
                              <StyledTableCell
                                key={idx}
                                align="center"
                                width={column.width}
                              >
                                <Typography>{column.title}</Typography>
                              </StyledTableCell>
                            ))}
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {currSkills.map((item, idx) => (
                            <TableRow key={idx}>
                              <StyledTableCell
                                width={getWidth("skill")}
                                align="center"
                              >
                                {item?.skill?.name && <Typography>{item.skill.name}</Typography>}
                              </StyledTableCell>

                              <StyledTableCell
                                width={getWidth("skillLevel")}
                                align="center"
                              >
                                {item?.skillLevel?.name && item?.skillLevel?.altName && (
                                  <Typography>
                                    {skillCategory.id === SKILLS_CATEGORIES.language.id
                                      ? item.skillLevel.altName
                                      : item.skillLevel.name}
                                  </Typography>
                                )}
                              </StyledTableCell>

                              <StyledTableCell
                                width={getWidth("yearsOfExperience")}
                                align="center"
                              >
                                <Typography>{item.yearsOfExperience}</Typography>
                              </StyledTableCell>

                              <StyledTableCell
                                width={getWidth("actions")}
                                actionsWidth={100}
                                align="center"
                              >
                                <DataActions>
                                  <AddEditRequiredPositionSkills
                                    effect={"edit"}
                                    item={item}
                                    handleNotifyParent={notifyParent}
                                    position={position}
                                    skillCategory={skillCategory}
                                    buttonVariant={"text"}
                                  />
                                </DataActions>
                              </StyledTableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    }
                  </TableContainer>
                </Container>
              </>
            )}
          </DialogContent>
        </LoadingOverlay>
      </Dialog>
    </>
  );
};

export default memo(PositionSkillsDetailsModal);
